import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Redirect } from "wouter";
import { AlertCircle, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "@/hooks/use-toast";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const fieldClass =
  "h-12 px-4 bg-slate-50/50 border-slate-200 focus:border-blue-800 focus:ring-4 focus:ring-blue-800/5 transition-all";

function LoginForm() {
  const { login } = useAuth();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = handleSubmit(async ({ username, password }) => {
    try {

      //setIsLoading(true);
      //setError(null);
      await login(username, password);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Invalid username or password";
      //setError(message);
      toast({ title: "Login Failed", description: message, variant: "destructive" });
    } finally {
      //setIsLoading(false);
    }
  });

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-[#f8fafc] overflow-hidden">
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -left-[5%] w-[60%] h-[60%] rounded-full bg-blue-800/10 blur-[120px]" />
        <div className="absolute top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-indigo-700/5 blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-[480px] px-6 py-10">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mb-6 relative">
            <img
              src="/logo.png"
              alt="Someaknea Tech"
              className="h-16 w-auto object-contain transition-transform hover:scale-105 duration-300"
            />
            <span className="absolute -top-1 -right-8 bg-blue-100 text-blue-800 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter border border-blue-200">
              Beta
            </span>
          </div>
          <p className="text-[16px] text-slate-500 max-w-[320px] leading-relaxed">
            Use your account to access the dashboard and manage everything.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(30,58,138,0.12)] border border-slate-100 p-8 md:p-12">
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900 mb-6">
            Sign in to Your Account
          </h1>

          <form onSubmit={onSubmit} noValidate className="space-y-6">
            {errors.root && (
              <div
                role="alert"
                className="bg-red-50 border border-red-100 text-red-700 text-sm p-4 rounded-xl flex items-center gap-3"
              >
                <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
                <span className="font-medium">{errors.root.message}</span>
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 ml-1" htmlFor="username">
                  Username or Email
                </label>
                <Input
                  {...register("username")}
                  id="username"
                  autoComplete="username"
                  placeholder="admin@system.com"
                  className={fieldClass}
                  disabled={isSubmitting}
                />
                {errors.username && (
                  <p className="text-xs text-red-500 mt-1 ml-1">{errors.username.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 ml-1" htmlFor="password">
                  Password
                </label>
                <Input
                  {...register("password")}
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className={fieldClass}
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1 ml-1">{errors.password.message}</p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 font-bold shadow-lg shadow-blue-900/20 group"
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="flex items-center justify-center gap-2 text-[#94a3b8] text-[11px] font-bold uppercase tracking-[0.15em] mb-4">
              <ShieldCheck className="h-4 w-4 text-blue-800/40" />
              Secure, Fast and Reliable
            </div>
            <p className="text-sm text-slate-600 text-center">
              Don't have account?{" "}
              <a
                href="https://t.me/+G_fJ4M26JIIxNDYy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-blue-800 hover:underline"
              >
                Contact our team
              </a>
            </p>
          </div>
        </div>

        <p className="mt-10 text-center text-slate-400 text-sm">
          © 2025 Someaknea Tech. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-blue-800 animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) return <Redirect to="/dashboard" replace />;

  return <LoginForm />;
}
